import { ColumnType } from '@bedrock/components/lib/Table';
import { groupBy, compact } from 'lodash';
import {
  DemandQuoteTableColumnDto,
  DemandStandardCatalogVo,
  DemandSupplierQuoteDiffDetailVo,
  DemandSupplierQuoteRowDto,
  DiffDetailString,
} from 'services/api';
import { $sType } from '#utils';
import { financial } from '#utils/number';
import { isBlank } from '@bedrock/utils';
import React from 'react';
import CharLengthEllipsis from '#components/CharLengthEllipsis';

export enum QuoteDetailDiffType {
  NONE = -1,
  CREATED = 0,
  DELETED = 1,
  MODIFIED = 2,
}

type CamelCaseStr<S extends string> = S extends `${infer First}_${infer Rest}`
  ? `${Lowercase<First>}${Capitalize<CamelCaseStr<Rest>>}`
  : Lowercase<S>;

type ToCamelCase<T> = {
  [K in keyof T as CamelCaseStr<K & string>]: T[K];
};

export type QuoteDetailValue<T = string | number> =
  | T
  | T[]
  | { supplierValue?: T; prefillValue?: T; diffType?: QuoteDetailDiffType };

export interface QuoteDetailColVo extends DemandQuoteTableColumnDto {
  children?: DemandQuoteTableColumnDto[];
}

export interface QuoteDetailVo extends ToCamelCase<DemandSupplierQuoteRowDto> {
  _id?: number;
  l2Cols?: string;
  l2$index?: number;
  _rowSpan?: Record<
    keyof ToCamelCase<DemandSupplierQuoteRowDto>,
    { span?: number; cooperationAccountNo?: number; l2No?: number }
  >;
}

export interface QuoteDetailDiffVo
  extends ToCamelCase<DemandSupplierQuoteDiffDetailVo> {
  _id?: number;
  _rowSpan?: Record<
    keyof ToCamelCase<DemandSupplierQuoteDiffDetailVo>,
    { span?: number; cooperationAccountNo?: number; l2No?: number }
  >;
}

export const COLUMN_WIDTH_BY_DATA_TYPE: Record<
  string,
  ColumnType<QuoteDetailVo>
> = {
  input: { width: 120 },
  text: { width: 400 },
  text_with_upload: { width: 400 },
  numeric: { width: 120, className: 'font-semibold' },
  dropdown_radio: { width: 200 },
  dropdown_list: { width: 200 },
  date_input: { width: 120 },
  date_range: { width: 280 },
};

export const COLUMN_CONFIG: Partial<
  Record<keyof QuoteDetailVo, ColumnType<QuoteDetailVo>>
> = {
  cooperateAccountId: {
    width: 240,
    fixed: 'left',
  },
  l2$index: {
    width: 42,
  },
  totalPrice: {
    width: 120,
    className: 'font-semibold no-border-right',
    align: 'right',
    fixed: 'right',
  },
};

export const getDisplayValue = (
  columnKey: string,
  value?: string | number,
  options?: { standardOptions?: Record<string, DemandStandardCatalogVo> }
) => {
  let displayValue = null;
  switch (columnKey) {
    case 'standardCode1':
    case 'standardCode2':
    case 'standardCode3': {
      const option = options?.standardOptions?.[value];
      if (!option) {
        return '--';
      }
      displayValue = option?.name ?? '--';
      break;
    }
    case 'travelCateringPrice':
    case 'unitPrice':
    case 'totalPrice': {
      displayValue = !isBlank(value) ? financial(value) : null;
      break;
    }
    default: {
      displayValue = value;
      break;
    }
  }

  const content = !isBlank(displayValue) ? displayValue : '--';
  return <CharLengthEllipsis value={content} />;
};

export const getQuoteDetailValue = (
  value?: QuoteDetailValue,
  column?: QuoteDetailColVo
) => {
  if (column?.data_type === 'text_with_upload') {
    return value;
  }
  if (Array.isArray(value)) {
    if (column.data_type === 'date_range') {
      return value.join('～');
    }
    return value.join('、');
  } else if (typeof value === 'object') {
    return value?.supplierValue ?? value?.prefillValue;
  } else {
    return value;
  }
};

export const getColumnsVo = (cols?: DemandQuoteTableColumnDto[]) => {
  const remoteCols = cols.map((col) => {
    return {
      code: col.code,
      label: col.label,
      data_type: col.data_type,
    } as QuoteDetailColVo;
  });
  const l2Cols = remoteCols?.filter((col) => col.code?.startsWith('l2$'));

  if (l2Cols?.length) {
    const l2ColKeys = l2Cols.map((col) => col.code);
    const firstL2Index = remoteCols?.findIndex(
      (col) => col.code === l2ColKeys[0]
    );

    remoteCols?.splice(firstL2Index, 0, {
      code: 'l2Cols',
      label: '分发平台',
      children: l2Cols,
    } as QuoteDetailColVo);

    return remoteCols.filter((col) => !l2ColKeys.includes(col.code));
  }

  if (!remoteCols.find((col) => col.code === 'cooperateAccountId')) {
    remoteCols.unshift({ code: 'cooperateAccountId', label: '合作内容' });
  }

  return remoteCols;
};

export const getQuotesVo = (
  columnsVo?: QuoteDetailColVo[],
  quoteData?:
    | ToCamelCase<DemandSupplierQuoteRowDto>[]
    | ToCamelCase<DemandSupplierQuoteDiffDetailVo>[],
  options?: { enableStandardColumnMerge?: boolean }
) => {
  const quoteDataVo = quoteData?.map((qd, index) => {
    return { ...qd, _id: index + 1, _rowSpan: {} } as QuoteDetailVo;
  });
  const rowSpanKeyChains: (keyof QuoteDetailVo)[] = [];

  if (columnsVo.find((cv) => cv.code === 'cooperateAccountId')) {
    rowSpanKeyChains.push('cooperateAccountId');
  }
  // 报价key, 用于合并行
  rowSpanKeyChains.push('quoteKey');

  if (options?.enableStandardColumnMerge) {
    if (columnsVo.find((cv) => cv.code === 'standardCode1')) {
      rowSpanKeyChains.push('standardCode1');
    }
    if (columnsVo.find((cv) => cv.code === 'standardCode2')) {
      rowSpanKeyChains.push('standardCode2');
    }
    if (columnsVo.find((cv) => cv.code === 'standardCode3')) {
      rowSpanKeyChains.push('standardCode3');
    }
  }

  const handleOneKey = (keyIndex: number, data?: QuoteDetailVo[]) => {
    const key = rowSpanKeyChains[keyIndex];

    if (!key) {
      return;
    }

    const column = columnsVo.find((cv) => cv.code === key);

    const keyGroups = groupBy(data, (item) => {
      const keyValue = getQuoteDetailValue(item[key as string], column);
      return keyValue;
    });

    Object.keys(keyGroups).forEach((groupKey, groupIndex) => {
      const keyGrouopValue = keyGroups[groupKey];

      keyGrouopValue.forEach((kgv, kgvIndex) => {
        if (!kgv._rowSpan[key]) {
          kgv._rowSpan[key] = {};
        }
        kgv._rowSpan[key].span = kgvIndex === 0 ? keyGrouopValue.length : 0;
        kgv._rowSpan[key].cooperationAccountNo = groupIndex + 1;
        kgv._rowSpan[key].l2No = kgvIndex + 1;
      });

      handleOneKey(keyIndex + 1, keyGrouopValue);
    });
  };

  handleOneKey(0, quoteDataVo);

  return quoteDataVo;
};

export const getStandardCodes = (
  quoteData?:
    | ToCamelCase<DemandSupplierQuoteRowDto>[]
    | ToCamelCase<DemandSupplierQuoteDiffDetailVo>[]
) => {
  const getValueCodes = (value?: string | ToCamelCase<DiffDetailString>) => {
    return compact(
      typeof value === 'string'
        ? [value]
        : [value?.prefillValue, value?.supplierValue]
    );
  };

  return (
    quoteData?.flatMap?.(
      (
        i:
          | ToCamelCase<DemandSupplierQuoteRowDto>
          | ToCamelCase<DemandSupplierQuoteDiffDetailVo>
      ) =>
        compact([
          ...getValueCodes(
            i.standardCode1 as string | ToCamelCase<DiffDetailString>
          ),
          ...getValueCodes(
            i.standardCode2 as string | ToCamelCase<DiffDetailString>
          ),
          ...getValueCodes(
            i.standardCode3 as string | ToCamelCase<DiffDetailString>
          ),
        ])
    ) ?? []
  );
};
